import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { authApi } from '../services/api'

interface User {
  id: number
  email: string
  role: string
  nome: string
  cognome: string
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (email: string, password: string) => Promise<{ requires_2fa: boolean; partial_token?: string }>
  loginWithToken: (data: { access_token: string; user_id: number; role: string; nome: string; cognome: string; email: string }) => void
  logout: () => void
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const savedToken = localStorage.getItem('xsophia_token')
    const savedUser = localStorage.getItem('xsophia_user')
    if (savedToken && savedUser) {
      setToken(savedToken)
      setUser(JSON.parse(savedUser))
      authApi.me().catch(() => {
        localStorage.removeItem('xsophia_token')
        localStorage.removeItem('xsophia_user')
        setToken(null)
        setUser(null)
      }).finally(() => setIsLoading(false))
    } else {
      setIsLoading(false)
    }
  }, [])

  const login = async (email: string, password: string) => {
    const res = await authApi.login(email, password)
    const data = res.data
    if (data.requires_2fa) {
      return { requires_2fa: true, partial_token: data.partial_token as string }
    }
    const userData: User = { id: data.user_id, email, role: data.role, nome: data.nome, cognome: data.cognome }
    setToken(data.access_token)
    setUser(userData)
    localStorage.setItem('xsophia_token', data.access_token)
    localStorage.setItem('xsophia_user', JSON.stringify(userData))
    return { requires_2fa: false }
  }

  const loginWithToken = (data: { access_token: string; user_id: number; role: string; nome: string; cognome: string; email: string }) => {
    const userData: User = { id: data.user_id, email: data.email, role: data.role, nome: data.nome, cognome: data.cognome }
    setToken(data.access_token)
    setUser(userData)
    localStorage.setItem('xsophia_token', data.access_token)
    localStorage.setItem('xsophia_user', JSON.stringify(userData))
  }

  const logout = () => {
    setToken(null)
    setUser(null)
    localStorage.removeItem('xsophia_token')
    localStorage.removeItem('xsophia_user')
  }

  return (
    <AuthContext.Provider value={{ user, token, login, loginWithToken, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
